import { Box, Chip, FormControl, ListItemText, Stack, Tooltip, Typography } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1AutocompleteMultipleSelect } from 'components/core/autocomplete/C1AutoCompleteMultipleSelect'
import { AppAvatar } from 'components/core/avatar'
import { AVATAR_SIZES } from 'components/core/avatar/AvatarSizes'
import { APIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { getEntitlementDescription } from 'components/core/search/apps'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { ExpiresInTimeChip } from 'components/core/time/display'
import { AccessRequest } from 'components/pages/accessrequest/create/form-v2/RequestAccessFormV2'
import { C1EntitlementDisplayName, getEntitlementDisplayNameTextOnly } from 'components/pages/entitlement/display-name'
import { GrantTaskType } from 'components/pages/task/type'
import { isPast } from 'date-fns'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { Check } from 'lucide-react'
import { RollupEntitlementsWithGrantsRequest, SearchRequestableEntitlementsForUserRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { RollupEntitlementsWithTasksRequest } from 'pbts/c1/api/task/v1/search'
import { Task, TaskTypeGrant } from 'pbts/c1/api/task/v1/task'
import { Timestamp as pbtsTimestamp } from 'pbts/google/protobuf/timestamp'
import { FC, useEffect, useMemo, useState } from 'react'
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { DecoratedEntitlementValue, rollupEntitlementsWithGrantsApi, searchRequestableEntitlementsForUserApi } from 'redux_toolkit/entitlements'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'
import { rollupEntitlementsWithTasksApi } from 'redux_toolkit/tasks'

type AccessRequestFormEntFieldProps = {
    control: Control<AccessRequest>
    errors: FieldErrors<AccessRequest>
}

const renderTags = (
    value: DecoratedEntitlementValue[],
    getTagProps,
): React.ReactElement[] => value.map((option, index) => <Chip
    sx={{ borderRadius: C1BorderRadius.regular }}
    key={`${option.id}-${option.appId}`}
    label={option && <Box py={1}>
        <C1EntitlementDisplayName
            entitlement={option}
            readOnly
            textTruncationWidth="32em"
        />
    </Box>}
    {...getTagProps({ index })}
/>)

const renderLabel = (opt: DecoratedEntitlementValue): string => (opt
    ? getEntitlementDisplayNameTextOnly(opt)
    : '')

type RenderExtraInfoProps = {
    opt: DecoratedEntitlementValue
}

const RenderExtraInfo: FC<RenderExtraInfoProps> = ({ opt }) => {
    const intl = useIntl()

    if (!opt) return null

    const expiresAt = opt.grant?.deprovisionAt
    const createdAt = opt.grant?.createdAt

    return <Stack direction="row" alignItems="center" spacing={1}>
        {createdAt && !expiresAt && <Tooltip
            title={intl.formatMessage({ id: 'components_time_expired', defaultMessage: 'You already have this access' })}
        >
            <C1Icon icon={Check} sx={{ color: 'success.main' }} />
        </Tooltip>}
        {(createdAt && expiresAt) && <ExpiresInTimeChip timestamp={expiresAt} showTooltip />}
        {!!opt.task?.numericId && <TaskViewLinkV2 task={Task.create({
            numericId: opt.task.numericId,
            id: opt.task.id,
            type: { taskType: { oneofKind: 'grant', grant: TaskTypeGrant.create({}) } },
        })}
        />}
    </Stack>
}

const renderOption = (props, entitlement: DecoratedEntitlementValue): React.ReactNode => {
    const sx = {
        ...props.sx,
        ...(props['aria-disabled'] === true ? {
            '.MuiAutocomplete-listbox &[aria-disabled="true"]': {
                opacity: 1,
                pointerEvents: 'all',
                cursor: 'default',

                '&.Mui-focused': { backgroundColor: 'transparent' },
            },
        } : {}),
    }

    const onClick = props['aria-disabled'] === true
        ? (e): void => { e.stopPropagation() }
        : props.onClick

    const disabledSx = props['aria-disabled'] === true ? {
        opacity: 0.7,
        pointerEvents: 'none',
    } : {}

    const entitlementDescription = getEntitlementDescription(entitlement)

    return <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        justifyContent="space-between"
        width="100%"
        component="li"
        {...props}
        key={`${entitlement.id}-${entitlement.appId}`}
        onClick={onClick}
        sx={sx}
    >
        {entitlement.id === SystemAnyAccessEntitlementID
            ? <Stack direction="row" alignItems="center" spacing={1} flexGrow={1} sx={disabledSx}>
                <AppAvatar appId={entitlement.appId} appName={entitlement.app?.displayName} size="xs" />
                <Typography>
                    <FormattedMessage id="AnyAccessEntitlement" defaultMessage="Application access" />
                </Typography>
            </Stack>
            : <Stack flexGrow={1} sx={{ ...disabledSx, minWidth: 0, overflow: 'hidden' }}>
                <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <C1EntitlementDisplayName entitlement={entitlement} readOnly />
                    </Stack>
                </Box>
                <ListItemText
                    sx={{ ml: `${AVATAR_SIZES.md.width}px`, pl: 1 }}
                    primary={entitlementDescription}
                    primaryTypographyProps={{ variant: 'caption', title: entitlementDescription, noWrap: true }}
                />
            </Stack>}
        <RenderExtraInfo opt={entitlement} />
    </Stack>
}

const getGroupByValue = (option: DecoratedEntitlementValue): string =>
    (option.id === SystemAnyAccessEntitlementID ? 'Basic app access' : 'All entitlements')

const sortOptionsByGroup = (options: DecoratedEntitlementValue[]): DecoratedEntitlementValue[] => {
    return [...options].sort((a, b) => {
        const groupA = getGroupByValue(a)
        const groupB = getGroupByValue(b)
        return groupB.localeCompare(groupA)
    })
}

const getOptionDisabled = (option: DecoratedEntitlementValue): boolean => {
    const expiresAt = option.grant?.deprovisionAt
    const createdAt = option.grant?.createdAt

    const isExpired = expiresAt ? isPast(pbtsTimestamp.toDate(expiresAt)) : undefined

    return !!(createdAt && !expiresAt && !isExpired) || !!(option.task?.id && option.task?.numericId)
}

export const AccessRequestFormEntFieldV2: FC<AccessRequestFormEntFieldProps> = ({ control, errors }) => {
    const intl = useIntl()
    const app = useWatch({
        name: 'app',
        control,
    })
    const user = useWatch({
        name: 'user',
        control,
    })

    const appUser = useWatch({
        name: 'appUser',
        control,
    })

    const disabled = !app?.id || !user?.id
    const targetUserId = user?.id
    const appId = app?.id

    const [query, setQuery] = useState('')

    const { data, isLoading, error } = useComposeEntitlements(appId, targetUserId, appUser?.id, query)

    const invGrants = rollupEntitlementsWithGrantsApi.useInvalidateRollupEntitlementsWithGrants()
    const invTasks = rollupEntitlementsWithTasksApi.useInvalidateRollupEntitlementsWithTasks()

    if (disabled) return null

    return <FormControl error={!!errors.ents} fullWidth>
        <Controller
            name="ents"
            control={control}
            render={({ field }): React.ReactElement => <C1AutocompleteMultipleSelect<DecoratedEntitlementValue>
                id="decoratedEnts"
                data-testid="access-request-entitlement-select"
                placeholder={intl.formatMessage({ id: 'SelectOneAppEntHook_placeholder', defaultMessage: ' Select an entitlement' })}
                resultList={data}
                loading={isLoading}
                label={intl.formatMessage({
                    id: 'component_access_request_entitlements_input_label',
                    defaultMessage: 'What access is needed?',
                })}
                renderOption={renderOption}
                renderTags={renderTags}
                error={!!errors.ents}
                onQueryChange={setQuery}
                helperText={errors.ents?.message}
                renderLabel={renderLabel}
                onSelected={(v: Array<DecoratedEntitlementValue> | null): void => field.onChange(v)}
                value={field.value}
                onBlur={field.onBlur}
                groupBy={getGroupByValue}
                filterOptions={(options): DecoratedEntitlementValue[] => sortOptionsByGroup(options)}
                getOptionDisabled={getOptionDisabled}
                onOpen={(): void => {
                    invGrants()
                    invTasks()
                }}
            />
            }
        />
        {!!error && <Box sx={{ mt: 2 }}><APIError err={error} /></Box>}
    </FormControl>
}

const useComposeEntitlements = (appId: string, userId: string, appUserId: string, query: string): {
    data: DecoratedEntitlementValue[];
    isLoading: boolean;
    error: Readonly<SerializedErrorType> | undefined;
} => {
    const req = useMemo(
        () => SearchRequestableEntitlementsForUserRequest.create({
            userId, appId, query, expandMask: { paths: ['*'] },
        }),
        [userId, appId, query],
    )

    const {
        data: initialData,
        isLoading,
        error,
    } = searchRequestableEntitlementsForUserApi.useSearchRequestableEntitlementsForUser(req, {
        actionable: !!(userId && appId),
    })

    const [entitlementsData, setEntitlementsData] = useState<DecoratedEntitlementValue[]>([])

    const entitlements = useMemo(() => {
        if (!initialData) return []
        return initialData.flat().map((ent) => AppEntitlementRef.create(ent))
    }, [initialData])

    const tasksReq = useMemo(
        () => RollupEntitlementsWithTasksRequest.create({ userId, appUserId, entitlements, taskTypes: [GrantTaskType] }),
        [userId, appUserId, entitlements],
    )

    const { data: taskData } = rollupEntitlementsWithTasksApi.useRollupEntitlementsWithTasks(tasksReq, {
        actionable: !!entitlements.length && !!(appUserId || userId),
    })

    const grantsReq = useMemo(
        () => RollupEntitlementsWithGrantsRequest.create({ appUserId, entitlements }),
        [appUserId, entitlements],
    )

    const { data: grantData } = rollupEntitlementsWithGrantsApi.useRollupEntitlementsWithGrants(grantsReq, {
        actionable: !!entitlements.length && !!appUserId,
    })

    useEffect(() => {
        const tempEntMap = new Map<string, DecoratedEntitlementValue>()

        if (initialData) {
            initialData.flat().forEach((ent) => {
                tempEntMap.set(ent.id, ent)
            })
        }

        if (taskData) {
            taskData.flat().forEach((task) => {
                const entitlement = tempEntMap.get(task.entitlementId)
                if (entitlement) {
                    tempEntMap.set(task.entitlementId, { ...entitlement, task })
                }
            })
        }

        if (grantData) {
            grantData.flat().forEach((grant) => {
                const entitlement = tempEntMap.get(grant.appEntitlementId)
                if (entitlement) {
                    tempEntMap.set(grant.appEntitlementId, { ...entitlement, grant })
                }
            })
        }

        setEntitlementsData(Array.from(tempEntMap.values()))
    }, [initialData, taskData, grantData])

    return {
        data: entitlementsData,
        isLoading,
        error,
    }
}
